import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Building2, CornerDownLeft } from 'lucide-react'
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Person } from '@/components/data/person'
import { PlanBadge } from '@/components/data/badges'
import { useDebounced } from '@/hooks/use-debounced'
import { useTeams, useUsers } from '@/lib/queries'
import { nav } from './nav'

/** ⌘K: jump to a page, a team or a person. Teams and users are searched on the server. */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate()
  const [text, setText] = useState('')
  const query = useDebounced(text.trim(), 200)
  const searching = open && query.length >= 2

  const teams = useTeams({ search: query, pageSize: '6' }, searching)
  const users = useUsers({ search: query, pageSize: '6' }, searching)

  useEffect(() => {
    if (!open) setText('')
  }, [open])

  const go = (to: string) => {
    onOpenChange(false)
    navigate(to)
  }
  const loading = searching && (teams.isFetching || users.isFetching)
  const teamItems = searching ? (teams.data?.items ?? []) : []
  const userItems = searching ? (users.data?.items ?? []) : []

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Search" description="Find a team, a person or a page">
      <Command shouldFilter={false}>
        <CommandInput value={text} onValueChange={setText} placeholder="Search teams, people, emails, Stripe ids…" />
        <CommandList className="max-h-96">
          {searching && !loading && teamItems.length === 0 && userItems.length === 0 && (
            <CommandEmpty>Nothing matches “{query}”.</CommandEmpty>
          )}
          {!searching && (
            <CommandGroup heading="Go to">
              {nav.map((n) => (
                <CommandItem key={n.to} value={n.label} onSelect={() => go(n.to)}>
                  <n.icon /> {n.label}
                </CommandItem>
              ))}
              <p className="px-2 py-2 text-xs text-muted-foreground">Type two letters or more to search teams and people.</p>
            </CommandGroup>
          )}
          {teamItems.length > 0 && (
            <CommandGroup heading="Teams">
              {teamItems.map((t) => (
                <CommandItem key={t.id} value={`team-${t.id}`} onSelect={() => go(`/teams/${t.id}`)}>
                  <Building2 />
                  <span className="truncate">{t.name}</span>
                  <span className="ml-auto flex items-center gap-2">
                    <PlanBadge planId={t.plan_id} name={t.plan_name} />
                    <CornerDownLeft className="size-3 opacity-40" />
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {userItems.length > 0 && (
            <CommandGroup heading="People">
              {userItems.map((u) => (
                <CommandItem key={u.uid} value={`user-${u.uid}`} onSelect={() => go(`/users?user=${encodeURIComponent(u.uid)}&search=${encodeURIComponent(u.email)}`)}>
                  <Person name={u.name} email={u.email} uid={u.uid} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  )
}
